import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'

import GroupProfileKeys from 'src/modules/group-profile/gp_keys'
import { WaitingScope } from '../../the-connection/tc_scopes'
import { GroupManagerFormKeys } from './form'
import GroupManagerKeys from './gm_keys'
import { GroupManagerScope, HeaderScope } from './gm_scopes'
import { GroupManagerService } from './gm_service'

const LOG = Logger.get('GroupManagerPresenter')
const groupManagerService = GroupManagerService.singleton()

export class GroupManagerPresenter extends CubePresenter<MainPresenter, GroupManagerScope> {
    // :: Fields

    private readonly __headerBar: Presenter<HeaderScope, GroupManagerPresenter>

    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new GroupManagerScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last: boolean
    ): Promise<boolean> {
        const keys = new GroupManagerKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
        }

        if (last) {
            keys.dialogSlot(undefined)
        }

        if (this.scope.groupFilter !== keys.groupFilter) {
            this.scope.groupFilter = keys.groupFilter || ''
        }

        if (!this.scope.groups || this.scope.groups.length === 0) {
            await this.#find()
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new GroupManagerKeys(this.app, intent)
        keys.groupFilter = this.scope.groupFilter || undefined
    }

    async #initialize() {
        LOG.debug('initialize')
        this.scope.onGroupFilterChanged = this.action(this.handleGroupFilterChanged)
        this.scope.onFind = this.action(this.handleFind)
        this.scope.onClearFilters = this.action(this.handleClearFilters)
        this.scope.onEdit = this.action(this.handleEdit)
        this.scope.onShowProfile = this.action(this.handleShowProfile)
        this.scope.onNew = this.action(this.handleNew)
        this.scope.onShowDeletedChange = this.action(this.handleShowDeletedChange)
        this.scope.onOpenRowMenu = this.action(this.handleOpenRowMenu)
        this.scope.onCloseRowMenu = this.action(this.handleCloseRowMenu)
        this.scope.onDeleteGroup = this.action(this.handleDeleteGroup)
        this.scope.onDeleteConfirmTextChanged = this.action(this.handleDeleteConfirmTextChanged)
        this.scope.onConfirmDeleteGroup = this.action(this.handleConfirmDeleteRestoreGroup)
        this.scope.onCancelConfirmDeleteGroup = this.action(this.handleCancelConfirmDeleteGroup)
    }

    private async handleGroupFilterChanged(nameFilter: string) {
        this.scope.groupFilter = nameFilter
    }

    private async handleClearFilters() {
        this.scope.groupFilter = ''
        this.scope.showDeleted = false
        await this.#find()
    }

    private async handleOpenRowMenu(groupId: number) {
        this.scope.rowMenuGroupId = groupId
    }

    private async handleCloseRowMenu() {
        this.scope.rowMenuGroupId = undefined
    }

    private async handleDeleteConfirmTextChanged(text: string) {
        this.scope.deleteConfirmText = text
    }

    private async handleFind() {
        await this.#find()
    }

    async #find() {
        try {
            this.scope.waiting = new WaitingScope()
            const groupRows = await groupManagerService.fetchGroups(this.scope.groupFilter, this.scope.showDeleted)
            this.scope.groups = groupRows
        } catch (caught) {
            LOG.error('find', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleEdit(groupId: number) {
        this.scope.rowMenuGroupId = undefined
        const keys = new GroupManagerFormKeys(this.app)
        keys.groupId = groupId
        await keys.flip()
    }

    private async handleShowProfile(groupId: number) {
        this.scope.rowMenuGroupId = undefined
        const keys = new GroupProfileKeys(this.app)
        keys.groupId = groupId
        await keys.flip()
    }

    private async handleNew() {
        await new GroupManagerFormKeys(this.app).flip()
    }

    private async handleShowDeletedChange() {
        this.scope.showDeleted = !this.scope.showDeleted
        await this.#find()
    }

    private async handleDeleteGroup(groupId: number) {
        const group = this.scope.groups.find((g) => Number(g.id) === groupId)
        this.scope.rowMenuGroupId = undefined
        this.scope.groupToDelete = group
        this.scope.deleteConfirmText = ''
        this.scope.deleteModalOpen = true
    }

    private async handleCancelConfirmDeleteGroup() {
        this.scope.deleteModalOpen = false
        this.scope.groupToDelete = undefined
        this.scope.deleteConfirmText = ''
    }

    private async handleConfirmDeleteRestoreGroup() {
        if (this.scope.groupToDelete && this.scope.groupToDelete.id) {
            try {
                this.scope.waiting = new WaitingScope()
                if (this.scope.groupToDelete.deleted_date) {
                    await groupManagerService.restoreGroup(this.scope.groupToDelete.id)
                    this.app.notify('success', 'Grupo restaurado com sucesso')
                } else {
                    await groupManagerService.deleteGroup(this.scope.groupToDelete.id)
                    this.app.notify('success', 'Grupo excluído com sucesso')
                }
                this.scope.deleteModalOpen = false
                this.scope.groupToDelete = undefined
                this.scope.deleteConfirmText = ''
                await this.#find()
            } catch (caught) {
                LOG.error('handleConfirmDeleteGroup', caught)
                throw caught
            } finally {
                this.scope.waiting = undefined
            }
        }
    }
}

export default GroupManagerPresenter
